import { css, keyframes } from '@emotion/react';

import type { TeleprompterSettings } from '@/hooks/use-teleprompter-settings';
import { darkTheme } from '@/theme/dark-theme';
import { mq, spacing } from '@/theme/tokens';

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

export interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  settings: TeleprompterSettings;
  onFontSizeChange: (v: number) => void;
  onLineWidthChange: (v: number) => void;
  onScrollSpeedChange: (v: number) => void;
  onMusicVolumeChange: (v: number) => void;
  onAutoScrollChange: (v: boolean) => void;
  onMirrorHorizontalChange: (v: boolean) => void;
  onMirrorVerticalChange: (v: boolean) => void;
  onReset: () => void;
  connectionStatus?: string;
  debugOpen?: boolean;
  onDebugToggle?: () => void;
}

const sectionLabelCss = css`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${darkTheme.textMuted};
  margin-bottom: 8px;
  padding-left: 12px;
  font-weight: 500;
`;

const cardCss = css`
  background: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  overflow: hidden;
`;

const dividerCss = css`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin: 0 14px;
`;

const sliderCss = css`
  width: 100%;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: ${darkTheme.border};
  border-radius: 2px;
  outline: none;
  accent-color: ${darkTheme.primary};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    background: ${darkTheme.text};
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    background: ${darkTheme.text};
    border-radius: 50%;
    cursor: pointer;
    border: none;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }

  &::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 2px;
  }
`;

function SliderRow({
  label,
  value,
  unit,
  min,
  max,
  ariaLabel,
  onChange,
}: {
  label: string;
  value: number;
  unit?: string;
  min: number;
  max: number;
  ariaLabel: string;
  onChange: (v: number) => void;
}) {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div
      css={css`
        padding: 12px 14px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        `}
      >
        <span
          css={css`
            font-size: 14px;
            color: ${darkTheme.text};
          `}
        >
          {label}
        </span>
        <span
          css={css`
            font-size: 13px;
            color: ${darkTheme.primary};
            font-weight: 500;
            font-variant-numeric: tabular-nums;
          `}
        >
          {value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        aria-label={ariaLabel}
        onChange={(e) => onChange(Number(e.target.value))}
        css={sliderCss}
        style={{
          background: `linear-gradient(to right, ${darkTheme.primary} 0%, ${darkTheme.primary} ${pct}%, ${darkTheme.border} ${pct}%, ${darkTheme.border} 100%)`,
        }}
      />
    </div>
  );
}

function ToggleSwitch({
  checked,
  ariaLabel,
  onClick,
}: {
  checked: boolean;
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onClick}
      css={css`
        width: 51px;
        height: 31px;
        border-radius: 16px;
        border: none;
        padding: 2px;
        cursor: pointer;
        background: ${checked ? darkTheme.primary : darkTheme.textMuted};
        position: relative;
        transition: background 0.2s ease;
        flex-shrink: 0;
      `}
    >
      <div
        css={css`
          width: 27px;
          height: 27px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          position: absolute;
          top: 2px;
          transition: left 0.2s ease;
          left: ${checked ? '22px' : '2px'};
        `}
      />
    </button>
  );
}

export function SettingsPanel({
  isOpen,
  onClose,
  settings,
  onFontSizeChange,
  onLineWidthChange,
  onScrollSpeedChange,
  onMusicVolumeChange,
  onAutoScrollChange,
  onMirrorHorizontalChange,
  onMirrorVerticalChange,
  onReset,
  connectionStatus,
  debugOpen,
  onDebugToggle,
}: SettingsPanelProps) {
  if (!isOpen) return null;

  return (
    <>
      <div
        data-testid="settings-backdrop"
        role="button"
        tabIndex={-1}
        aria-label="Close settings panel"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        css={css`
          position: fixed;
          inset: 0;
          z-index: 29;
          background: rgba(0, 0, 0, 0.4);
        `}
      />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        data-testid="settings-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-panel-title"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        css={css`
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 100vw;
          background: ${darkTheme.surface};
          border-left: 1px solid ${darkTheme.border};
          z-index: 30;
          display: flex;
          flex-direction: column;
          padding: 20px;
          gap: 0;
          overflow-y: auto;
          animation: ${slideIn} 0.2s ease-out;

          ${mq.sm} {
            width: 320px;
          }
        `}
      >
        {/* Header */}
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 24px;
          `}
        >
          <h3
            id="settings-panel-title"
            css={css`
              margin: 0;
              font-size: 17px;
              font-weight: 600;
              color: ${darkTheme.text};
              letter-spacing: -0.2px;
            `}
          >
            Settings
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            css={css`
              width: 28px;
              height: 28px;
              background: ${darkTheme.border};
              border: none;
              border-radius: 50%;
              color: ${darkTheme.textSecondary};
              cursor: pointer;
              font-size: 14px;
              display: flex;
              align-items: center;
              justify-content: center;
              line-height: 1;

              &:hover {
                color: ${darkTheme.text};
              }
            `}
          >
            ×
          </button>
        </div>

        {/* Display section */}
        <div
          css={css`
            margin-bottom: 20px;
          `}
        >
          <div css={sectionLabelCss}>Display</div>
          <div css={cardCss}>
            <SliderRow
              label="Font Size"
              value={settings.fontSize}
              unit="px"
              min={24}
              max={96}
              ariaLabel="Font size"
              onChange={onFontSizeChange}
            />
            <hr css={dividerCss} />
            <SliderRow
              label="Line Width"
              value={settings.lineWidth}
              unit="em"
              min={15}
              max={40}
              ariaLabel="Line width"
              onChange={onLineWidthChange}
            />
            <hr css={dividerCss} />
            <div
              css={css`
                padding: 14px;
                display: flex;
                justify-content: space-between;
                align-items: center;
              `}
            >
              <span
                css={css`
                  font-size: 14px;
                  color: ${darkTheme.text};
                `}
              >
                Mirror Horizontal
              </span>
              <ToggleSwitch
                checked={settings.mirrorHorizontal}
                ariaLabel="Mirror horizontal"
                onClick={() => onMirrorHorizontalChange(!settings.mirrorHorizontal)}
              />
            </div>
            <hr css={dividerCss} />
            <div
              css={css`
                padding: 14px;
                display: flex;
                justify-content: space-between;
                align-items: center;
              `}
            >
              <span
                css={css`
                  font-size: 14px;
                  color: ${darkTheme.text};
                `}
              >
                Mirror Vertical
              </span>
              <ToggleSwitch
                checked={settings.mirrorVertical}
                ariaLabel="Mirror vertical"
                onClick={() => onMirrorVerticalChange(!settings.mirrorVertical)}
              />
            </div>
          </div>
        </div>

        {/* Playback section */}
        <div
          css={css`
            margin-bottom: 20px;
          `}
        >
          <div css={sectionLabelCss}>Playback</div>
          <div css={cardCss}>
            <SliderRow
              label="Scroll Speed"
              value={settings.scrollSpeed}
              min={10}
              max={100}
              ariaLabel="Scroll speed"
              onChange={onScrollSpeedChange}
            />
            <hr css={dividerCss} />
            <SliderRow
              label="Music Volume"
              value={settings.musicVolume}
              unit="%"
              min={0}
              max={100}
              ariaLabel="Music volume"
              onChange={onMusicVolumeChange}
            />
            <hr css={dividerCss} />
            <div
              css={css`
                padding: 14px;
                display: flex;
                justify-content: space-between;
                align-items: center;
              `}
            >
              <span
                css={css`
                  font-size: 14px;
                  color: ${darkTheme.text};
                `}
              >
                Auto-scroll
              </span>
              <ToggleSwitch
                checked={settings.autoScroll}
                ariaLabel="Auto-scroll"
                onClick={() => onAutoScrollChange(!settings.autoScroll)}
              />
            </div>
          </div>
        </div>

        {/* Connection status */}
        {connectionStatus && (
          <div
            data-testid="connection-status"
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 4px 12px 16px;
            `}
          >
            <span
              css={css`
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: ${connectionStatus === 'Connected'
                  ? darkTheme.success
                  : darkTheme.textSecondary};
              `}
            />
            <span
              css={css`
                font-size: 13px;
                color: ${darkTheme.textMuted};
              `}
            >
              {connectionStatus}
            </span>
          </div>
        )}

        {/* Developer section */}
        {onDebugToggle && (
          <div
            css={css`
              margin-bottom: 24px;
            `}
          >
            <div css={sectionLabelCss}>Developer</div>
            <div css={cardCss}>
              <div
                css={css`
                  padding: 14px;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                `}
              >
                <div>
                  <span
                    css={css`
                      font-size: 14px;
                      color: ${darkTheme.text};
                    `}
                  >
                    Debug Console
                  </span>
                  <div
                    css={css`
                      font-size: 11px;
                      color: ${darkTheme.textMuted};
                      margin-top: 2px;
                    `}
                  >
                    {`\u2318\u21E7D`}
                  </div>
                </div>
                <ToggleSwitch
                  checked={!!debugOpen}
                  ariaLabel="Debug Console"
                  onClick={onDebugToggle}
                />
              </div>
            </div>
          </div>
        )}

        {/* Reset Defaults */}
        <div
          css={css`
            margin-top: auto;
            text-align: center;
            padding: ${spacing.sm} 0;
          `}
        >
          <button
            type="button"
            onClick={onReset}
            css={css`
              background: none;
              border: none;
              color: ${darkTheme.error};
              font-size: 14px;
              cursor: pointer;
              padding: ${spacing.sm} ${spacing.md};
              min-height: 44px;

              &:hover {
                opacity: 0.8;
              }
            `}
          >
            Reset Defaults
          </button>
        </div>
      </div>
    </>
  );
}
